import { EncryptedResponse } from '../service-worker/encryption/encrypted-response.model';
import { serviceWorkerUserCacheIdHeader } from '../service-worker/service-worker-cache-header.interceptor';
import { serviceWorkerFetchStrategyHeader } from '../service-worker/service-worker-fetch-strategy-header.constant';
import { WorkloadQueryParameter } from '../shared/constants/workload-query-parameter.enum';
import { FetchStrategy } from '../shared/instrumentation/instrumented-http.service';
import { getDigest } from '../shared/utilities/digest.utility';
import { decode, encode } from '../shared/utilities/encoding.utility';
import { verifyEncryptionVersion } from '../shared/utilities/encryption-version-verification.utility';
import { decryptData } from '../shared/utilities/encryption.utility';
import { importKey } from '../shared/utilities/import-key.utility';
import { unregisterAndReload } from '../shared/utilities/unregister-and-reload.utility';
import { IdentityApiResponse } from './identity-api-response.model';

function applyParamFromWindow(urlToMutate: URL, paramName: string) {
    const windowSearchParams = new URLSearchParams(window.location.search);
    const windowValue = windowSearchParams.get(paramName);

    if (!windowValue) {
        return;
    }

    urlToMutate.searchParams.set(paramName, windowValue);
}

function applyAllParamsFromWindow(urlToMutate: URL, skipParams: Array<string> = []) {
    const windowSearchParams = new URLSearchParams(window.location.search);

    windowSearchParams.forEach((value, key) => {
        if (skipParams.includes(key)) {
            return;
        }

        urlToMutate.searchParams.set(key, value);
    });
}

async function getShellAndConfigData(userCacheId: string, strategy: FetchStrategy, puid: string): Promise<Response> {
    try {
        const shellAndConfigApiUrl = new URL(`${window.location.origin}/api/shellandconfigdata`);

        // It's important to keep these values in the same order as used in 'officehome-service-worker-authorized-install.service.ts'
        // This is to make sure we can apply window params that will use the same order as the cached API request.
        //
        applyParamFromWindow(shellAndConfigApiUrl, 'mockauth');
        applyParamFromWindow(shellAndConfigApiUrl, 'envType');
        applyParamFromWindow(shellAndConfigApiUrl, 'flight');

        // Workload parameter must be appended last, because it's set in 'shell-and-config.warmup.ts', where it's appended last.
        shellAndConfigApiUrl.searchParams.set('workload', WorkloadQueryParameter.OfficeHome);

        const response = await fetch(shellAndConfigApiUrl.toString(), {
            headers: {
                [serviceWorkerUserCacheIdHeader]: userCacheId,
                [serviceWorkerFetchStrategyHeader]: strategy,
                ['X-OfficeHome-UserId']: puid
            }
        });

        return response.ok ? response : null;
    } catch {
        return null;
    }
}

export async function appShellBoot() {
    try {
        const identityUrl = new URL(`${window.location.origin}/api/identity`);

        applyAllParamsFromWindow(identityUrl);

        const identityResponse = await fetch(identityUrl.toString());

        if (!identityResponse.ok) {
            await unregisterAndReload();
            return;
        }

        const parsedIdentityResponse: IdentityApiResponse = await identityResponse.json();

        const { tenantId, puid, encryptionConfig } = parsedIdentityResponse;

        const userId = await getDigest(
            window.crypto.subtle,
            `${tenantId}_${puid}`,
            encryptionConfig.digestSaltBase64,
            (toEncode: string) => encode(toEncode, window)
        );

        let cachedShellAndConfigData = await getShellAndConfigData(userId, FetchStrategy.CacheOnly, puid);

        if (cachedShellAndConfigData) {
            try {
                const encryptedBodyBufferWithVersion = await cachedShellAndConfigData.arrayBuffer();

                const encryptedBodyBuffer = verifyEncryptionVersion(new Uint8Array(encryptedBodyBufferWithVersion));

                if (encryptedBodyBuffer) {
                    const key = await importKey(encryptionConfig.cryptoKey, window.crypto.subtle);

                    const originalResponse = await decryptData<EncryptedResponse>(
                        encryptedBodyBuffer,
                        key,
                        window.crypto,
                        (toDecode: Uint8Array) => decode(toDecode, window)
                    );

                    const asDecryptedResponse = new Response(originalResponse.body, {
                        headers: new Headers(JSON.parse(originalResponse.headers)),
                        status: originalResponse.status,
                        statusText: originalResponse.statusText
                    });

                    const jsonBody = await asDecryptedResponse.json();

                    window.shellAndConfigResponse = Promise.resolve(jsonBody);
                }
            } catch {
                // Failed to load cached shell and config data, set to null indicating that it's unavailable.
                cachedShellAndConfigData = null;
            }
        }

        // Fire the network request to update Shell and Config data, regardless of the acquisition of the cached data.
        const networkShellAndConfigDataTask = getShellAndConfigData(userId, FetchStrategy.NetworkFirst, puid);

        if (!cachedShellAndConfigData) {
            const networkShellAndConfigData = await networkShellAndConfigDataTask;

            // In this case, we couldn't obtain any cached data for this build, or network data for this build.
            // We unregister the active service worker, and reload the page to get them back to the regular non-appshell flow.
            if (!networkShellAndConfigData) {
                await unregisterAndReload();
            } else {
                // Try to use the Network data if available.
                const jsonBody = await networkShellAndConfigData.json();
                window.shellAndConfigResponse = Promise.resolve(jsonBody);
            }
        }

        const splashScreen = document.getElementById('splash-screen');
        splashScreen.remove();
    } catch {
        await unregisterAndReload();
    }
}
